if (!customElements.get('blueprint-access-gate')) {
  customElements.define(
    'blueprint-access-gate',
    class BlueprintAccessGate extends HTMLElement {
      abortController = undefined;

      constructor() {
        super();
        this.onSubmit = this.onSubmit.bind(this);
      }

      connectedCallback() {
        this.form = this.querySelector('.lp-template-blueprint-section-1__gate-form');
        this.emailInput = this.querySelector('[data-blueprint-gate-email]');
        this.submitButton = this.querySelector('[data-blueprint-gate-submit]');
        this.message = this.querySelector('[data-blueprint-gate-message]');

        if (this.form) {
          this.form.setAttribute('novalidate', 'novalidate');
          this.form.addEventListener('submit', this.onSubmit);
        }

        this.clearLegacyLocalStorage();

        if (this.dataset.serverAccess === 'true' || document.documentElement.classList.contains('blueprint-has-access')) {
          this.unlock();
          return;
        }

        this.bootstrapAccess();
        document.documentElement.classList.add('blueprint-access-ready');
      }

      disconnectedCallback() {
        this.abortController?.abort();

        if (this.form) {
          this.form.removeEventListener('submit', this.onSubmit);
        }
      }

      bootstrapAccess() {
        const cachedAccess = this.readCookieAccess();

        if (!this.hasValidAccess(cachedAccess)) {
          document.documentElement.classList.remove('blueprint-has-access');
          return;
        }

        if (this.emailInput && !this.emailInput.value) {
          this.emailInput.value = cachedAccess.email;
        }

        this.unlock();
      }

      async onSubmit(event) {
        event.preventDefault();

        const email = this.normalizedEmail;

        if (!this.form?.reportValidity()) {
          return;
        }

        this.subscribeShopifyCustomer();

        await this.subscribeEmail(email);
      }

      async subscribeEmail(email) {
        this.setLoading(true);
        this.showMessage('', 'hidden');

        try {
          const didGrantAccess = await this.requestAccess(email);

          if (!didGrantAccess) {
            this.clearAccess();
            this.lock();
            this.showMessage('Something went wrong. Please try again.', 'error');
            return;
          }

          this.persistAccess({
            email,
            subscribed: true,
            verifiedAt: new Date().toISOString(),
          });
          this.unlock();
          this.showMessage('Access unlocked. Welcome to the Blueprint Library.', 'success');
        } catch (error) {
          this.clearAccess();
          this.lock();
          this.showMessage('Something went wrong. Please try again.', 'error');
        } finally {
          this.setLoading(false);
        }
      }

      subscribeShopifyCustomer() {
        const shopifyForm = this.form;

        if (!shopifyForm) {
          return;
        }

        this.submitShopifyFormAjax(shopifyForm);
      }

      submitShopifyFormAjax(shopifyForm) {
        const body = this.buildShopifyFormBody(shopifyForm);
        const action = shopifyForm.getAttribute('action');

        if (!body || !action) {
          return;
        }

        try {
          fetch(action, {
            method: 'POST',
            body,
            credentials: 'same-origin',
            keepalive: true,
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
              Accept: 'text/html, */*; q=0.01',
              'X-Requested-With': 'XMLHttpRequest',
            },
          });
        } catch (error) {
          /* Shopify subscribe should not block Beehiiv access unlock. */
        }
      }

      buildShopifyFormBody(shopifyForm) {
        const params = new URLSearchParams();
        const inputs = shopifyForm.querySelectorAll('[name]');

        for (const input of inputs) {
          if (input.disabled || !input.name) {
            continue;
          }

          if ((input.type === 'radio' || input.type === 'checkbox') && !input.checked) {
            continue;
          }

          params.append(input.name, input.value);
        }

        return params.toString();
      }

      async requestAccess(email) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        const response = await fetch(this.dataset.apiUrl, {
          method: 'POST',
          signal: this.abortController.signal,
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            tags: this.dataset.apiTags || 'beehiiv, creator',
          }),
        });

        return response.ok;
      }

      hasValidAccess(access) {
        return access?.subscribed === true && (Boolean(access?.email) || access?.source === 'newsletter');
      }

      readCookieAccess() {
        const cookiePrefix = `${this.encodedCookieName}=`;

        try {
          const cookies = document.cookie.split('; ');

          for (const cookie of cookies) {
            if (!cookie.startsWith(cookiePrefix)) {
              continue;
            }

            return JSON.parse(decodeURIComponent(cookie.slice(cookiePrefix.length)));
          }
        } catch (error) {
          return null;
        }

        return null;
      }

      persistAccess(accessPayload) {
        const serializedAccess = JSON.stringify(accessPayload);

        try {
          const secureFlag = window.location.protocol === 'https:' ? '; Secure' : '';

          document.cookie = `${this.encodedCookieName}=${encodeURIComponent(serializedAccess)}; Max-Age=31536000; Path=/; SameSite=Lax${secureFlag}`;
        } catch (error) {
          /* Cookies unavailable. */
        }
      }

      clearAccess() {
        try {
          document.cookie = `${this.encodedCookieName}=; Max-Age=0; Path=/; SameSite=Lax`;
        } catch (error) {
          /* no-op */
        }

        document.documentElement.classList.remove('blueprint-has-access');
      }

      clearLegacyLocalStorage() {
        try {
          localStorage.removeItem(this.cookieName);
        } catch (error) {
          /* no-op */
        }
      }

      unlock() {
        document.documentElement.classList.add('blueprint-has-access', 'blueprint-access-ready');
        this.classList.add('is-blueprint-unlocked');
        this.applyUnlockClasses();
        requestAnimationFrame(() => this.applyUnlockClasses());
      }

      lock() {
        document.documentElement.classList.remove('blueprint-has-access');
        this.classList.remove('is-blueprint-unlocked');
        this.querySelector('.lp-template-blueprint-section-1')?.classList.remove('is-blueprint-unlocked');
        this.closest('.shopify-section')?.classList.remove('is-blueprint-unlocked');
      }

      applyUnlockClasses() {
        this.querySelector('.lp-template-blueprint-section-1')?.classList.add('is-blueprint-unlocked');
        this.closest('.shopify-section')?.classList.add('is-blueprint-unlocked');
      }

      setLoading(isLoading) {
        if (!this.submitButton) {
          return;
        }

        this.submitButton.disabled = isLoading;
        this.submitButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      }

      showMessage(text, type) {
        if (!this.message) {
          return;
        }

        if (type === 'hidden' || !text) {
          this.message.hidden = true;
          this.message.textContent = '';
          this.message.className = 'lp-template-blueprint-section-1__gate-message';
          return;
        }

        this.message.hidden = false;
        this.message.textContent = text;
        this.message.className = `lp-template-blueprint-section-1__gate-message lp-template-blueprint-section-1__gate-message--${type}`;
      }

      get normalizedEmail() {
        return (this.emailInput?.value || '').trim().toLowerCase();
      }

      get cookieName() {
        return this.dataset.cookieName || 'blueprintAccess';
      }

      get encodedCookieName() {
        return encodeURIComponent(this.cookieName);
      }
    }
  );
}
